import type { V3 } from "@istari/istari-client";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { agentQueryOptions } from "~/api/agent/agent-query-options";
import type { Notification } from "~/notifications/model/notification";
import { resourceQueryOptions } from "~/resource/data-source/api/resource-query-options";
import { userQueryOptions } from "~/user/data-source/user-query-options";

import { toJobStatus, toSharedResourceType, toSharedRole } from "./api/inbox-adapter";
import { notificationPollingQueryOptions } from "./api/notification-polling-query-options";
import { getAgentFallbackId, toNotificationActorName } from "./resolve-notification-actor-name";

export function useNotificationPolling(options?: { enabled?: boolean }) {
  const pollingQuery = useQuery({
    ...notificationPollingQueryOptions.list(),
    enabled: options?.enabled,
  });

  const shareEvents = (pollingQuery.data?.items ?? [])
    .filter((item) => item.data.eventType === "notification.user.access.granted")
    .map((item) => item.data as V3.UserAccessGrantedNotificationEvent);

  // combine applies replaceEqualDeep to the result, keeping stable references when data is unchanged.
  // This prevents useMemo below from recomputing on every render.
  const resourceDetails = useQueries({
    queries: shareEvents.map((event) => resourceQueryOptions.byId(event.resourceId)),
    combine: (results) =>
      results.map((r) => {
        if (r.status === "error") {
          console.error("[notifications] resource lookup failed", r.error);
          return { name: null, resourceType: null, createdById: null };
        }
        return {
          name: r.data?.name ?? null,
          resourceType: r.data?.resourceType ?? null,
          createdById: r.data?.createdById ?? null,
        };
      }),
  });

  const createdByIds = resourceDetails.map((rd) => rd.createdById);

  const actorUsers = useQueries({
    queries: createdByIds.map((createdById) => ({
      ...userQueryOptions.byId(createdById ?? ""),
      enabled: !!createdById,
    })),
    combine: (results) =>
      results.map((result) => {
        if (result.status === "error") {
          console.error("[notifications] actor lookup failed", result.error);
          return null;
        }
        return result.data ?? null;
      }),
  });

  const agentFallbackIds = useMemo(() => actorUsers.map(getAgentFallbackId), [actorUsers]);

  const agentDisplayNames = useQueries({
    queries: agentFallbackIds.map((agentId) => ({
      ...agentQueryOptions.byId(agentId ?? ""),
      enabled: !!agentId,
    })),
    combine: (results) =>
      results.map((result) => {
        if (result.status === "error") {
          console.error("[notifications] agent lookup failed", result.error);
          return null;
        }
        return result.data?.displayName ?? null;
      }),
  });

  const actorNames = useMemo(
    () => actorUsers.map((user, index) => toNotificationActorName(user) ?? agentDisplayNames[index] ?? null),
    [actorUsers, agentDisplayNames],
  );

  const notifications = useMemo(() => {
    if (!pollingQuery.data) return undefined;
    return buildNotifications(pollingQuery.data.items, actorNames, resourceDetails);
  }, [pollingQuery.data, actorNames, resourceDetails]);

  return { ...pollingQuery, data: notifications };
}

function buildNotifications(
  rawItems: V3.NotificationDto[],
  actorNames: (string | null)[],
  resourceDetails: { name: string | null; resourceType: V3.ResourceTypeDto | null; createdById: string | null }[],
): Notification[] {
  const notifications: Notification[] = [];
  let shareIdx = 0;

  for (const item of rawItems) {
    const { data } = item;

    if (data.eventType === "notification.user.access.granted") {
      const actorName = actorNames[shareIdx] ?? null;
      const resource = resourceDetails[shareIdx] ?? null;
      shareIdx++;
      notifications.push(toShareNotification(data, actorName, resource));
    } else if (data.eventType === "notification.user.job.terminal_state") {
      const notification = toJobNotification(data);
      if (notification) notifications.push(notification);
    }
  }

  return notifications;
}

function toShareNotification(
  data: V3.UserAccessGrantedNotificationEvent,
  actorName: string | null,
  resource: { name: string | null; resourceType: V3.ResourceTypeDto | null; createdById: string | null } | null,
): Notification {
  const actorId = resource?.createdById ?? data.grantedUserId;
  const displayName = actorName ?? "Unknown user";

  return {
    id: `${data.grantedUserId}:${data.resourceId}:${data.createdAt?.toISOString() ?? crypto.randomUUID()}`,
    type: "share",
    createdAt: data.createdAt ?? new Date(),
    status: "unread",
    triggeredBy: { userId: actorId, displayName },
    payload: {
      resourceType: toSharedResourceType(resource?.resourceType) ?? "file",
      resourceId: data.resourceId,
      resourceName: resource?.name ?? data.resourceId,
      role: toSharedRole(data.permissionLevel),
      shareCount: 1,
    },
  };
}

function toJobNotification(data: V3.UserJobTerminalStateNotificationEvent): Notification | null {
  const jobStatus = toJobStatus(data.terminalState);
  if (!jobStatus) return null;

  return {
    id: `job:${data.jobId}:${data.createdAt?.toISOString() ?? crypto.randomUUID()}`,
    type: "job",
    createdAt: data.createdAt ?? new Date(),
    status: "unread",
    triggeredBy: { userId: "system", displayName: "Build Pipeline" },
    payload: {
      jobId: data.jobId,
      jobStatus,
      functionName: `Job ${data.jobId.slice(0, 8)}`,
    },
  };
}
